import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Upload, FileText, Award, Clock, AlertTriangle, CheckCircle2, GraduationCap, Download, PlayCircle, Trophy } from 'lucide-react';
import { academyApi, STATUS_LABELS, STATUS_TONES } from './api';
import { academyInternalApi } from './apiInternal';
import AcademyInternalPlayer from './AcademyInternalPlayer';
import AcademyInternalCertificate from './AcademyInternalCertificate';

const ProgressBar = ({ value }) => (
  <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
    <div
      className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all"
      style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
    />
  </div>
);

const AcademyMyTrainings = ({ refreshKey, onCertUploaded }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [certNumber, setCertNumber] = useState('');
  const fileRef = useRef(null);
  const [internalPlayerForId, setInternalPlayerForId] = useState(null);
  const [internalCertData, setInternalCertData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await academyApi.myProgress();
      setData(res);
    } catch (e) {
      toast.error("Erreur lors du chargement de votre parcours.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [refreshKey]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingFor) return;
    try {
      await academyApi.uploadCertificate(uploadingFor.id, file, certNumber);
      toast.success("Certificat déposé. En attente de validation.");
      setUploadingFor(null);
      setCertNumber('');
      load();
      if (onCertUploaded) onCertUploaded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'upload.");
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const triggerUpload = (enrollment) => {
    setUploadingFor(enrollment);
    setTimeout(() => fileRef.current?.click(), 10);
  };

  const startTraining = (t) => {
    // Ouvre le lien externe AVANT l'appel API (préserve le geste utilisateur
    // pour éviter le blocage popup).
    if (t.external_url) {
      window.open(t.external_url, '_blank', 'noopener,noreferrer');
    }
    academyApi.startEnrollment(t.id)
      .then(() => {
        toast.success("Inscription enregistrée.");
        load();
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Erreur.");
      });
  };

  if (loading && !data) return <div className="text-center text-slate-500 py-8">Chargement…</div>;
  if (!data) return null;

  const { kpi, enrollments = [], mandatory_open = [] } = data;

  return (
    <div className="space-y-5" data-testid="academy-my-trainings">
      <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={handleFile} data-testid="academy-cert-file-input" />

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
            Obligatoires validées
          </div>
          <div className="text-2xl font-bold text-slate-800 mt-1" data-testid="academy-kpi-validated-mandatory">
            {kpi.validated_mandatory} / {kpi.total_mandatory}
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Award className="w-3.5 h-3.5 text-amber-500" />
            Points cumulés
          </div>
          <div className="text-2xl font-bold text-slate-800 mt-1" data-testid="academy-kpi-points">
            {kpi.total_points}
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Clock className="w-3.5 h-3.5 text-amber-500" />
            En attente de validation
          </div>
          <div className="text-2xl font-bold text-slate-800 mt-1" data-testid="academy-kpi-pending">
            {kpi.pending_review}
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
            Expirées
          </div>
          <div className="text-2xl font-bold text-slate-800 mt-1" data-testid="academy-kpi-expired">
            {kpi.expired}
          </div>
        </CardContent></Card>
      </div>

      {/* Progress */}
      <Card><CardContent className="p-5">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="font-semibold text-slate-900">Progression annuelle</h3>
            <p className="text-xs text-slate-500">
              {kpi.validated_mandatory} formation{kpi.validated_mandatory > 1 ? 's' : ''} obligatoire{kpi.validated_mandatory > 1 ? 's' : ''} validée{kpi.validated_mandatory > 1 ? 's' : ''} sur {kpi.total_mandatory || 0}
            </p>
          </div>
          <div className="text-2xl font-bold text-emerald-600" data-testid="academy-progress-pct">{kpi.progress_pct} %</div>
        </div>
        <ProgressBar value={kpi.progress_pct} />
      </CardContent></Card>

      {/* Mandatory open */}
      {mandatory_open.length > 0 && (
        <div>
          <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            Formations obligatoires à démarrer ({mandatory_open.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {mandatory_open.map((t) => (
              <Card key={t.id} className="border-red-200">
                <CardContent className="p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900">{t.title}</div>
                    <div className="text-xs text-slate-500">{t.organism || '—'} · {t.duration}</div>
                  </div>
                  <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => startTraining(t)} data-testid={`academy-start-mandatory-${t.id}`}>
                    Commencer
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* My enrollments */}
      <div>
        <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
          <GraduationCap className="w-4 h-4 text-emerald-600" />
          Mes inscriptions ({enrollments.length})
        </h3>
        {enrollments.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-slate-500">
            Vous n&apos;avez démarré aucune formation. Rendez-vous dans le Catalogue.
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {enrollments.map((e) => (
              <Card key={e.id} data-testid={`academy-enrollment-${e.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-slate-900">{e.training?.title || 'Formation supprimée'}</div>
                        <Badge className={STATUS_TONES[e.status] || ''}>{STATUS_LABELS[e.status]}</Badge>
                        {e.training?.mandatory && <Badge className="bg-red-100 text-red-700 border-red-200">Obligatoire</Badge>}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {e.training?.organism || '—'} · Commencée le {e.started_at?.slice(0, 10)}
                        {e.expires_at && <> · Expire le {e.expires_at.slice(0, 10)}</>}
                      </div>
                      {e.certificate && (
                        <div className="text-xs text-slate-600 mt-1 flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          {e.certificate.filename}
                          {e.certificate.number && <span className="ml-1 text-slate-500">· N° {e.certificate.number}</span>}
                        </div>
                      )}
                      {e.status === 'refusee' && e.rejection_reason && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                          <strong>Refus :</strong> {e.rejection_reason}
                        </div>
                      )}
                      {e.status === 'validee' && e.validation_comment && (
                        <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-700">
                          <strong>Note admin :</strong> {e.validation_comment}
                        </div>
                      )}
                      {e.status === 'en_cours' && e.validation_comment && (
                        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                          <strong>Document demandé :</strong> {e.validation_comment}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {(() => {
                        const isInternal = (e.training?.organism || '').toLowerCase() === 'sada academy';
                        if (isInternal) {
                          return (
                            <>
                              {e.status !== 'validee' && e.status !== 'refusee' && (
                                <Button
                                  size="sm"
                                  onClick={() => setInternalPlayerForId(e.training?.id)}
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  data-testid={`academy-continue-internal-${e.id}`}
                                >
                                  <PlayCircle className="w-3.5 h-3.5 mr-1" />
                                  {e.status === 'certificat_soumis' ? 'Quiz réussi · attente admin' : 'Continuer le module'}
                                </Button>
                              )}
                              {e.status === 'validee' && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                    onClick={async () => {
                                      try {
                                        const cd = await academyInternalApi.certificateData(e.id);
                                        setInternalCertData(cd);
                                      } catch (err) {
                                        toast.error("Certificat indisponible.");
                                      }
                                    }}
                                    data-testid={`academy-view-internal-cert-${e.id}`}
                                  >
                                    <Trophy className="w-3.5 h-3.5 mr-1" /> Mon certificat SADA
                                  </Button>
                                  <Badge className="bg-emerald-600 text-white">+{e.points_earned} pts</Badge>
                                </>
                              )}
                            </>
                          );
                        }
                        // Formations externes : dépôt certificat classique
                        return (
                          <>
                            {(e.status === 'en_cours' || e.status === 'refusee') && (
                              <div className="flex flex-col items-end gap-1">
                                <Input
                                  placeholder="N° certificat (optionnel)"
                                  className="text-xs h-8 w-48"
                                  onChange={(ev) => setCertNumber(ev.target.value)}
                                />
                                <Button size="sm" onClick={() => triggerUpload(e)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`academy-upload-cert-${e.id}`}>
                                  <Upload className="w-3.5 h-3.5 mr-1" />
                                  Déposer certificat
                                </Button>
                              </div>
                            )}
                            {e.certificate && (
                              <a
                                href={academyApi.downloadCertificateUrl(e.id)}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-emerald-600 hover:underline inline-flex items-center gap-1"
                                data-testid={`academy-download-cert-${e.id}`}
                              >
                                <Download className="w-3 h-3" /> Voir mon certificat
                              </a>
                            )}
                            {e.status === 'validee' && (
                              <Badge className="bg-emerald-600 text-white">+{e.points_earned} pts</Badge>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {internalPlayerForId && (
        <AcademyInternalPlayer
          trainingId={internalPlayerForId}
          onClose={() => { setInternalPlayerForId(null); load(); }}
        />
      )}
      {internalCertData && (
        <AcademyInternalCertificate
          data={internalCertData}
          onClose={() => setInternalCertData(null)}
        />
      )}
    </div>
  );
};

export default AcademyMyTrainings;
